<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { storeToRefs } from "pinia";

import {
  addProjectHazard,
  archiveProject,
  archiveProjectHazard,
  getProject,
  lookupPostcode,
  updateProject,
  updateProjectLocation,
} from "@/api/client";
import PageLayout from "@/components/PageLayout.vue";
import { useProjectStore } from "@/stores/project";

const router = useRouter();
const queryClient = useQueryClient();
const { selectedProjectId } = storeToRefs(useProjectStore());

const { data, isPending, isError, error } = useQuery({
  queryKey: ["project", selectedProjectId],
  queryFn: () =>
    selectedProjectId.value
      ? getProject(selectedProjectId.value)
      : Promise.reject(new Error("No project")),
  enabled: () => !!selectedProjectId.value,
});

// Common ECS service areas — suggestions only (free text, not a fixed list).
const SERVICE_AREAS = ["Rail", "Power", "Civils"];

const form = ref({ name: "", code: "", client_name: "", service_area: "" });
watch(
  data,
  (p) => {
    if (p) {
      form.value = {
        name: p.name ?? "",
        code: p.code ?? "",
        client_name: p.client_name ?? "",
        service_area: p.service_area ?? "",
      };
    }
  },
  { immediate: true },
);

const saving = ref(false);
const saved = ref(false);
const saveError = ref<string | null>(null);

async function save(): Promise<void> {
  if (!selectedProjectId.value || !form.value.name.trim()) return;
  saving.value = true;
  saved.value = false;
  saveError.value = null;
  try {
    await updateProject(selectedProjectId.value, {
      name: form.value.name.trim(),
      code: form.value.code.trim(),
      client_name: form.value.client_name.trim(),
      service_area: form.value.service_area.trim(),
    });
    await queryClient.invalidateQueries({ queryKey: ["project"] });
    await queryClient.invalidateQueries({ queryKey: ["projects"] });
    saved.value = true;
  } catch (e) {
    saveError.value = e instanceof Error ? e.message : String(e);
  } finally {
    saving.value = false;
  }
}

// ---- Site location (powers the dashboard's live weather) ----
const loc = ref<{
  postcode: string;
  location_name: string | null;
  latitude: number | null;
  longitude: number | null;
}>({ postcode: "", location_name: null, latitude: null, longitude: null });
watch(
  data,
  (p) => {
    if (p) {
      loc.value = {
        postcode: p.postcode ?? "",
        location_name: p.location_name ?? null,
        latitude: p.latitude ?? null,
        longitude: p.longitude ?? null,
      };
    }
  },
  { immediate: true },
);

const lookingUp = ref(false);
const lookupError = ref<string | null>(null);
async function lookup(): Promise<void> {
  const pc = loc.value.postcode.trim();
  if (!pc) return;
  lookingUp.value = true;
  lookupError.value = null;
  try {
    const r = await lookupPostcode(pc);
    loc.value = {
      postcode: r.postcode,
      location_name: r.place_name,
      latitude: r.latitude,
      longitude: r.longitude,
    };
  } catch (e) {
    lookupError.value = e instanceof Error ? e.message : String(e);
    loc.value.location_name = null;
    loc.value.latitude = null;
    loc.value.longitude = null;
  } finally {
    lookingUp.value = false;
  }
}

const savingLoc = ref(false);
const savedLoc = ref(false);
async function saveLocation(): Promise<void> {
  if (!selectedProjectId.value) return;
  savingLoc.value = true;
  savedLoc.value = false;
  try {
    await updateProjectLocation(selectedProjectId.value, {
      postcode: loc.value.postcode.trim() || null,
      location_name: loc.value.location_name,
      latitude: loc.value.latitude,
      longitude: loc.value.longitude,
    });
    await queryClient.invalidateQueries({ queryKey: ["project"] });
    await queryClient.invalidateQueries({ queryKey: ["project-weather"] });
    savedLoc.value = true;
  } finally {
    savingLoc.value = false;
  }
}

async function clearLocation(): Promise<void> {
  loc.value = { postcode: "", location_name: null, latitude: null, longitude: null };
  await saveLocation();
}

// --- Site hazards register ---
// Common site hazards — suggestions for the dropdown; any text is accepted.
const HAZARD_CATEGORIES = [
  "Asbestos",
  "High voltage",
  "Overhead lines",
  "Buried services",
  "Contaminated land",
  "Confined space",
  "Working at height",
  "Deep excavation",
  "Water / flooding",
  "Traffic / plant",
  "Other",
];
const activeHazards = computed(() =>
  (data.value?.hazards ?? []).filter((h) => !h.archived_at),
);
const hazardForm = ref({ category: HAZARD_CATEGORIES[0], location: "", description: "" });
const hazardBusy = ref(false);
const hazardError = ref<string | null>(null);

async function addHazard(): Promise<void> {
  if (!selectedProjectId.value || !hazardForm.value.description.trim()) return;
  hazardBusy.value = true;
  hazardError.value = null;
  try {
    await addProjectHazard(selectedProjectId.value, {
      category: hazardForm.value.category.trim() || "Other",
      location: hazardForm.value.location.trim() || undefined,
      description: hazardForm.value.description.trim(),
    });
    await queryClient.invalidateQueries({ queryKey: ["project"] });
    hazardForm.value = {
      category: HAZARD_CATEGORIES[0],
      location: "",
      description: "",
    };
  } catch (e) {
    hazardError.value = e instanceof Error ? e.message : String(e);
  } finally {
    hazardBusy.value = false;
  }
}

async function archiveHazard(hazardId: string): Promise<void> {
  if (!selectedProjectId.value) return;
  await archiveProjectHazard(selectedProjectId.value, hazardId);
  await queryClient.invalidateQueries({ queryKey: ["project"] });
}

const archiving = ref(false);
async function archive(): Promise<void> {
  if (!selectedProjectId.value) return;
  if (!window.confirm("Archive this project? It moves to the Archived tab and can be restored.")) {
    return;
  }
  archiving.value = true;
  try {
    await archiveProject(selectedProjectId.value);
    await queryClient.invalidateQueries({ queryKey: ["projects"] });
    void router.push({ name: "projects" });
  } finally {
    archiving.value = false;
  }
}
</script>

<template>
  <PageLayout>
    <h1 class="text-xl font-semibold mb-1">
      Settings
    </h1>
    <p class="text-sm text-neutral-500 mb-6">
      Project details and configuration.
    </p>

    <div
      v-if="isPending"
      class="text-neutral-500"
    >
      Loading…
    </div>
    <div
      v-else-if="isError"
      class="bg-semantic-danger/10 text-semantic-danger rounded-md p-4"
    >
      Failed to load: {{ error?.message }}
    </div>
    <div
      v-else-if="data"
      class="grid max-w-xl gap-8"
    >
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <h2 class="mb-4 font-semibold">
          Project details
        </h2>
        <div class="grid gap-4">
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Name</span>
            <input
              v-model="form.name"
              class="rounded-md border border-neutral-300 px-3 py-2"
            >
          </label>
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Project / contract code</span>
            <input
              v-model="form.code"
              class="rounded-md border border-neutral-300 px-3 py-2"
            >
          </label>
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Client</span>
            <input
              v-model="form.client_name"
              class="rounded-md border border-neutral-300 px-3 py-2"
            >
          </label>
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Service area</span>
            <input
              v-model="form.service_area"
              list="service-areas"
              placeholder="e.g. Rail"
              class="rounded-md border border-neutral-300 px-3 py-2"
            >
            <datalist id="service-areas">
              <option
                v-for="a in SERVICE_AREAS"
                :key="a"
                :value="a"
              />
            </datalist>
          </label>
        </div>
        <div class="mt-4 flex items-center gap-3">
          <button
            type="button"
            class="rounded-md bg-brand-primary px-4 py-2 font-semibold text-brand-primary-on transition hover:opacity-90 disabled:opacity-50"
            :disabled="saving || !form.name.trim()"
            @click="save"
          >
            {{ saving ? "Saving…" : "Save changes" }}
          </button>
          <span
            v-if="saved"
            class="text-sm text-semantic-success"
          >Saved.</span>
          <span
            v-if="saveError"
            class="text-sm text-semantic-danger"
          >{{ saveError }}</span>
        </div>
      </section>

      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <h2 class="mb-1 font-semibold">
          Site location
        </h2>
        <p class="mb-4 text-sm text-neutral-500">
          Set the project's postcode to power the dashboard's live weather
          forecast. This is for planning — the site diary's recorded weather
          stays as the operative enters it on the day.
        </p>
        <div class="grid gap-4">
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Postcode</span>
            <div class="flex gap-2">
              <input
                v-model="loc.postcode"
                placeholder="e.g. B5 4UA"
                class="flex-1 rounded-md border border-neutral-300 px-3 py-2 uppercase"
                @keyup.enter="lookup"
              >
              <button
                type="button"
                class="rounded-md border border-brand-primary/40 px-4 py-2 text-sm font-medium text-brand-primary hover:bg-brand-primary/5 disabled:opacity-50"
                :disabled="lookingUp || !loc.postcode.trim()"
                @click="lookup"
              >
                {{ lookingUp ? "Looking up…" : "Look up" }}
              </button>
            </div>
            <span
              v-if="lookupError"
              class="text-sm text-semantic-danger"
            >{{ lookupError }}</span>
          </label>

          <div
            v-if="loc.latitude !== null && loc.longitude !== null"
            class="flex items-center gap-2 rounded-md bg-brand-primary/5 px-3 py-2 text-sm"
          >
            <span class="text-brand-primary">📍</span>
            <span class="font-medium">{{ loc.location_name }}</span>
            <span class="text-neutral-400">
              {{ loc.latitude.toFixed(4) }}, {{ loc.longitude.toFixed(4) }}
            </span>
          </div>
        </div>
        <div class="mt-4 flex items-center gap-3">
          <button
            type="button"
            class="rounded-md bg-brand-primary px-4 py-2 font-semibold text-brand-primary-on transition hover:opacity-90 disabled:opacity-50"
            :disabled="savingLoc || loc.latitude === null"
            @click="saveLocation"
          >
            {{ savingLoc ? "Saving…" : "Save location" }}
          </button>
          <button
            v-if="data.latitude !== null"
            type="button"
            class="text-sm text-neutral-500 hover:text-semantic-danger"
            :disabled="savingLoc"
            @click="clearLocation"
          >
            Clear
          </button>
          <span
            v-if="savedLoc"
            class="text-sm text-semantic-success"
          >Saved.</span>
        </div>
      </section>

      <!-- Site hazards register -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <h2 class="mb-1 font-semibold">
          Site hazards
        </h2>
        <p class="mb-4 text-sm text-neutral-500">
          Site-specific hazards (asbestos register, HV cables, contaminated
          land, etc.). Entered here, they feed into RAMS and permits — typed
          once, used everywhere.
        </p>

        <div class="grid gap-3 rounded-lg border border-neutral-100 bg-neutral-50 p-4 sm:grid-cols-[1fr_1fr]">
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Category</span>
            <input
              v-model="hazardForm.category"
              list="hazard-categories"
              type="text"
              class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
            >
            <datalist id="hazard-categories">
              <option
                v-for="c in HAZARD_CATEGORIES"
                :key="c"
                :value="c"
              />
            </datalist>
          </label>
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Location (optional)</span>
            <input
              v-model="hazardForm.location"
              type="text"
              placeholder="e.g. Zone B, basement riser"
              class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
            >
          </label>
          <label class="grid gap-1 text-sm sm:col-span-2">
            <span class="text-neutral-500">Description</span>
            <textarea
              v-model="hazardForm.description"
              rows="2"
              placeholder="What specifically, where, severity. e.g. 'Asbestos cement panels in roof void per R&D survey 2024-03'"
              class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
            />
          </label>
          <div class="flex items-center gap-3 sm:col-span-2">
            <button
              type="button"
              class="rounded-md bg-brand-primary px-3 py-2 text-sm font-semibold text-brand-primary-on transition hover:opacity-90 disabled:opacity-50"
              :disabled="hazardBusy || !hazardForm.description.trim()"
              @click="addHazard"
            >
              {{ hazardBusy ? "Adding…" : "Add hazard" }}
            </button>
            <span
              v-if="hazardError"
              class="text-sm text-semantic-danger"
            >{{ hazardError }}</span>
          </div>
        </div>

        <p class="mb-2 mt-5 text-xs font-semibold uppercase tracking-wide text-neutral-400">
          {{ activeHazards.length }} active hazard(s)
        </p>
        <p
          v-if="!activeHazards.length"
          class="text-sm text-neutral-400"
        >
          No hazards recorded yet.
        </p>
        <ul
          v-else
          class="grid gap-2"
        >
          <li
            v-for="h in activeHazards"
            :key="h.hazard_id"
            class="flex items-start justify-between gap-3 rounded-lg border border-neutral-200 p-3"
          >
            <div class="min-w-0">
              <div class="flex flex-wrap items-center gap-2">
                <span class="rounded bg-semantic-warning/10 px-2 py-0.5 text-xs font-semibold text-semantic-warning">
                  {{ h.category }}
                </span>
                <span
                  v-if="h.location"
                  class="text-xs text-neutral-500"
                >📍 {{ h.location }}</span>
              </div>
              <p class="mt-1 text-sm text-neutral-800">
                {{ h.description }}
              </p>
            </div>
            <button
              type="button"
              class="shrink-0 text-xs text-neutral-400 hover:text-semantic-danger"
              @click="archiveHazard(h.hazard_id)"
            >
              Archive
            </button>
          </li>
        </ul>
      </section>

      <section class="rounded-xl border border-semantic-danger/30 bg-neutral-0 p-6">
        <h2 class="mb-1 font-semibold text-semantic-danger">
          Danger zone
        </h2>
        <p class="mb-4 text-sm text-neutral-500">
          Archiving moves the project to the Archived tab. Its data and history
          are kept, and it can be restored at any time.
        </p>
        <button
          type="button"
          class="rounded-md border border-semantic-danger/40 px-4 py-2 text-sm font-medium text-semantic-danger hover:bg-semantic-danger/5 disabled:opacity-50"
          :disabled="archiving"
          @click="archive"
        >
          {{ archiving ? "Archiving…" : "Archive project" }}
        </button>
      </section>
    </div>
  </PageLayout>
</template>
